import { useCallback, useEffect, useRef, useState } from 'react';

export interface DeferredUnmount<K extends string> {
    heldKeys: readonly K[];
    hold: (key: K) => void;
    release: (key: K) => void;
}

/**
 * Keeps a key mounted for `durationMs` after the caller has logically removed it, so an exit
 * transition has something to animate — React would otherwise tear the element out on the same
 * commit that closes it, and CSS cannot transition a node that no longer exists.
 *
 * The unmount rides a timer rather than `transitionend` because the duration is a constant the
 * caller already owns (it writes the CSS), and a real transition can be interrupted, cancelled, or
 * never fire at all on a hidden tab — a timer would be needed as the guard regardless, so it is
 * also the whole mechanism. `release` re-opens a key mid-exit and drops it immediately.
 */
export function useDeferredUnmount<K extends string>(durationMs: number): DeferredUnmount<K> {
    const [heldKeys, setHeldKeys] = useState<readonly K[]>([]);
    const timers = useRef(new Map<K, ReturnType<typeof setTimeout>>());

    useEffect(
        () => () => {
            timers.current.forEach(clearTimeout);
            timers.current.clear();
        },
        [],
    );

    const release = useCallback((key: K) => {
        clearTimeout(timers.current.get(key));
        timers.current.delete(key);
        setHeldKeys((previous) => (previous.includes(key) ? previous.filter((candidate) => candidate !== key) : previous));
    }, []);

    const hold = useCallback(
        (key: K) => {
            clearTimeout(timers.current.get(key));
            timers.current.set(
                key,
                setTimeout(() => release(key), durationMs),
            );
            setHeldKeys((previous) => (previous.includes(key) ? previous : [...previous, key]));
        },
        [release, durationMs],
    );

    return { heldKeys, hold, release };
}
